import { useCallback, useEffect, useMemo, useState } from "react";
import {
  localCalendarDate,
  type ActivityLog,
  type ActivityType,
  type Household,
  type Membership,
  type UserProfile,
  type UserSettings,
  DEFAULT_USER_SETTINGS,
} from "@lifelog/shared";
import { Calendar, Download, RefreshCw, Search, Trash2, X } from "lucide-react";
import { api, ApiError, isOfflineError } from "../lib/api.js";
import { clearOnEscape } from "../lib/clearOnEscape.js";
import { downloadExportBundle, formatFieldValue } from "../lib/export.js";
import { dayRelativeLabel, relativeTime, ONE_DAY_MS } from "../lib/time.js";
import { useDebounced } from "../lib/useDebounced.js";
import { BackfillModal } from "./BackfillModal.js";
import { ItemReportModal } from "./ItemReportModal.js";
import { ConfirmDialog } from "./ModalShell.js";
import { PhotoLightbox, PhotoThumb } from "./PhotoField.js";
import { ListSkeleton } from "./Skeleton.js";

const LOOKBACK_MS = 3 * 365 * 24 * 60 * 60 * 1000;

function canDeleteLog(log: ActivityLog, userId: string | undefined): boolean {
  if (log.visibility === "shared") return true;
  return Boolean(userId && log.ownerUserId === userId);
}

function haystack(log: ActivityLog, type: ActivityType | undefined): string {
  const parts: string[] = [type?.name ?? "", type?.shortName ?? "", log.note ?? ""];
  const fields = type?.fields ?? [];
  for (const [fieldId, value] of Object.entries(log.values)) {
    const field = fields.find((f) => f.fieldId === fieldId);
    parts.push(fieldId, field?.label ?? "", String(value), formatFieldValue(field, value));
  }
  return parts.join(" ").toLowerCase();
}

function groupByDay(
  logs: ActivityLog[],
  timezone: string,
): [string, string, ActivityLog[]][] {
  const map = new Map<string, { label: string; items: ActivityLog[] }>();
  for (const log of logs) {
    const key = localCalendarDate(log.occurredAt, timezone);
    let group = map.get(key);
    if (!group) {
      group = {
        label: new Date(key + "T12:00:00Z").toLocaleDateString(undefined, {
          weekday: "long",
          year: "numeric",
          month: "long",
          day: "numeric",
          timeZone: "UTC",
        }),
        items: [],
      };
      map.set(key, group);
    }
    group.items.push(log);
  }
  return Array.from(map.entries()).map(([key, { label, items }]) => [key, label, items]);
}

function logLine(log: ActivityLog, type: ActivityType | undefined): string {
  const fields = [...(type?.fields ?? [])].sort((a, b) => a.order - b.order);
  const bits: string[] = [];
  for (const field of fields) {
    const value = log.values[field.fieldId];
    if (value === undefined) continue;
    bits.push(`${field.label} ${formatFieldValue(field, value)}`);
  }
  if (log.note) bits.push(log.note);
  return bits.join(" · ");
}

export function LogsPane({ household }: { household: Household }) {
  const [logs, setLogs] = useState<ActivityLog[]>([]);
  const [types, setTypes] = useState<ActivityType[]>([]);
  const [members, setMembers] = useState<Membership[]>([]);
  const [me, setMe] = useState<UserProfile | null>(null);
  const [settings, setSettings] = useState<UserSettings>(DEFAULT_USER_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [typeId, setTypeId] = useState("");
  const [search, setSearch] = useState("");
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [reportType, setReportType] = useState<ActivityType | null>(null);
  const [pendingDelete, setPendingDelete] = useState<ActivityLog | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [lightboxKey, setLightboxKey] = useState<string | null>(null);

  const debouncedSearch = useDebounced(search, 350);
  const q = (search === "" ? "" : debouncedSearch).trim().toLowerCase();

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    setError(null);
    try {
      const from = new Date(Date.now() - LOOKBACK_MS).toISOString();
      const to = new Date().toISOString();
      const [logsRes, typesRes, membersRes, meRes, settingsRes] = await Promise.all([
        api<{ logs: ActivityLog[] }>(
          `/logs?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}&limit=500`,
        ),
        api<{ types: ActivityType[] }>("/activity-types"),
        api<{ members: Membership[] }>("/household/members"),
        api<{ profile: UserProfile }>("/me"),
        api<UserSettings>("/settings"),
      ]);
      setLogs(logsRes.logs);
      setTypes(typesRes.types);
      setMembers(membersRes.members);
      setMe(meRes.profile);
      setSettings(settingsRes);
    } catch (err) {
      if (isOfflineError(err)) {
        setError("You're offline. Try again when you're back online.");
      } else if (err instanceof ApiError && err.status === 401) {
        return;
      } else {
        setError(err instanceof Error ? err.message : "Couldn't load logs.");
      }
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, household.householdId]);

  const typeById = useMemo(() => {
    const m = new Map<string, ActivityType>();
    for (const t of types) m.set(t.activityTypeId, t);
    return m;
  }, [types]);

  const nameById = useMemo(() => {
    const m = new Map<string, string>();
    for (const mem of members) m.set(mem.userId, mem.displayName);
    return m;
  }, [members]);
  const nameOf = (id: string | undefined) => (id ? (nameById.get(id) ?? id) : "");

  const filterTypes = useMemo(() => {
    const ids = new Set(types.map((t) => t.activityTypeId));
    for (const log of logs) ids.add(log.activityTypeId);
    return [...ids]
      .map((id) => typeById.get(id))
      .filter((t): t is ActivityType => Boolean(t))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [types, logs, typeById]);

  const shown = useMemo(() => {
    return logs.filter((log) => {
      if (typeId && log.activityTypeId !== typeId) return false;
      if (q && !haystack(log, typeById.get(log.activityTypeId)).includes(q)) return false;
      return true;
    });
  }, [logs, typeId, q, typeById]);

  const days = useMemo(() => groupByDay(shown, household.timezone), [shown, household.timezone]);

  async function confirmDelete() {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api(
        `/logs/${encodeURIComponent(pendingDelete.occurredAt)}/${encodeURIComponent(pendingDelete.logId)}`,
        { method: "DELETE" },
      );
      setLogs((prev) => prev.filter((l) => l.logId !== pendingDelete.logId));
      setPendingDelete(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Couldn't delete that entry.");
      setPendingDelete(null);
    } finally {
      setDeleting(false);
    }
  }

  function exportShown() {
    const usedIds = new Set(shown.map((l) => l.activityTypeId));
    const usedTypes = [...usedIds]
      .map((id) => typeById.get(id))
      .filter((t): t is ActivityType => Boolean(t));
    downloadExportBundle(usedTypes, shown, nameOf);
  }

  // All hooks above this return (H-1).
  if (loading) {
    return (
      <div className="mx-auto flex h-full max-w-3xl flex-col p-4">
        <h1 className="mb-4 text-lg font-semibold text-gray-900 dark:text-gray-100">Logs</h1>
        <ListSkeleton rows={5} />
      </div>
    );
  }

  return (
    <div className="mx-auto flex h-full max-w-3xl flex-col p-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h1 className="flex-1 text-lg font-semibold text-gray-900 dark:text-gray-100">Logs</h1>
        <select
          value={typeId}
          onChange={(e) => setTypeId(e.target.value)}
          className="w-28 rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 sm:w-auto dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100"
          aria-label="Filter by activity"
        >
          <option value="">All types</option>
          {filterTypes.map((t) => (
            <option key={t.activityTypeId} value={t.activityTypeId}>
              {t.name}
            </option>
          ))}
        </select>
        <button
          type="button"
          onClick={exportShown}
          disabled={shown.length === 0}
          className="cursor-pointer rounded-md border border-gray-300 p-2 text-gray-700 hover:bg-gray-100 disabled:opacity-40 sm:p-1.5 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          title="Export bundle"
          aria-label="Export bundle"
        >
          <Download size={15} />
        </button>
        <button
          type="button"
          onClick={() => void load()}
          className="cursor-pointer rounded-md border border-gray-300 p-2 text-gray-700 hover:bg-gray-100 sm:p-1.5 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
          title="Refresh"
          aria-label="Refresh logs"
        >
          <RefreshCw size={15} />
        </button>
        <button
          type="button"
          onClick={() => setBackfillOpen(true)}
          className="min-h-11 rounded-lg border border-gray-200 px-3 text-sm text-gray-700 dark:border-gray-700 dark:text-gray-300"
        >
          Backfill
        </button>
      </div>

      <div className="mb-3 flex items-center gap-2 rounded-md border border-gray-300 bg-white px-2.5 py-2 sm:py-1.5 dark:border-gray-700 dark:bg-gray-800">
        <Search size={15} className="shrink-0 text-gray-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={clearOnEscape(search, setSearch)}
          placeholder="Search name, note, or field values…"
          className="min-w-0 flex-1 bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none dark:text-gray-100"
          aria-label="Search logs"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch("")}
            className="shrink-0 rounded p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            title="Clear search"
            aria-label="Clear search"
          >
            <X size={14} />
          </button>
        )}
      </div>

      {error && (
        <div className="mb-2 rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-950 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="min-h-0 flex-1 overflow-y-auto">
        {shown.length === 0 ? (
          <p className="mt-6 text-center text-sm text-gray-400">
            {search || typeId ? "No entries match your filters." : "No entries yet."}
          </p>
        ) : (
          <div className="space-y-4">
            {days.map(([dateKey, dateLabel, dayLogs]) => (
              <div key={dateKey}>
                <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-gray-300 bg-gray-50 px-2 py-1.5 dark:border-gray-700 dark:bg-gray-950">
                  <h3 className="min-w-0 flex-1 truncate text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {dateLabel}
                    <span className="ml-2 font-normal normal-case tracking-normal text-gray-400 dark:text-gray-500">
                      {dayRelativeLabel(dayLogs[0]!.occurredAt)}
                    </span>
                  </h3>
                  <span className="shrink-0 text-xs text-gray-400 dark:text-gray-500">
                    {dayLogs.length} {dayLogs.length === 1 ? "entry" : "entries"}
                  </span>
                </div>
                <ul className="divide-y divide-gray-100 dark:divide-gray-800/60">
                  {dayLogs.map((log) => {
                    const type = typeById.get(log.activityTypeId);
                    const recent = Date.now() - new Date(log.occurredAt).getTime() < ONE_DAY_MS;
                    const time = new Date(log.occurredAt).toLocaleTimeString(undefined, {
                      hour: "numeric",
                      minute: "2-digit",
                    });
                    const summary = logLine(log, type);
                    const who = nameOf(log.performedByUserId);
                    const openReport = type ? () => setReportType(type) : undefined;
                    return (
                      <li key={log.logId} className="group py-2 text-sm">
                        <div className="flex items-start gap-2 sm:hidden">
                          <div className="min-w-0 flex-1">
                            <div className="text-xs text-gray-500 dark:text-gray-400">
                              {time}
                              {recent && (
                                <span className="text-gray-400 dark:text-gray-500">
                                  {" "}
                                  ({relativeTime(log.occurredAt)})
                                </span>
                              )}
                            </div>
                            <div className="mt-0.5">
                              <span className="font-medium text-gray-800 dark:text-gray-200">
                                {type?.name ?? "Activity"}
                              </span>
                              {summary && (
                                <span className="text-gray-600 dark:text-gray-400"> — {summary}</span>
                              )}
                              {who && (
                                <span className="block text-xs text-gray-400">by {who}</span>
                              )}
                              {log.photoKey && (
                                <PhotoThumb photoKey={log.photoKey} onOpen={() => setLightboxKey(log.photoKey!)} />
                              )}
                            </div>
                          </div>
                          {openReport && (
                            <button
                              type="button"
                              onClick={openReport}
                              className="shrink-0 cursor-pointer rounded p-2 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
                              title="View report"
                              aria-label="View item report"
                            >
                              <Calendar size={15} />
                            </button>
                          )}
                          {canDeleteLog(log, me?.userId) && (
                            <button
                              type="button"
                              onClick={() => setPendingDelete(log)}
                              className="shrink-0 cursor-pointer rounded p-2 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950"
                              title="Delete"
                              aria-label="Delete entry"
                            >
                              <Trash2 size={15} />
                            </button>
                          )}
                        </div>
                        <div className="hidden items-center gap-3 sm:flex">
                          {openReport ? (
                            <button
                              type="button"
                              onClick={openReport}
                              className="shrink-0 cursor-pointer rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
                              title="View report"
                              aria-label="View item report"
                            >
                              <Calendar size={15} />
                            </button>
                          ) : (
                            <span className="w-[23px] shrink-0" />
                          )}
                          <span className="w-36 shrink-0 text-gray-500 dark:text-gray-400">
                            {time}
                            {recent && (
                              <span className="text-gray-400 dark:text-gray-500">
                                {" "}
                                ({relativeTime(log.occurredAt)})
                              </span>
                            )}
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="font-medium text-gray-800 dark:text-gray-200">
                              {type?.name ?? "Activity"}
                            </span>
                            {summary && (
                              <span className="text-gray-600 dark:text-gray-400"> — {summary}</span>
                            )}
                            {who && <span className="ml-2 text-xs text-gray-400">by {who}</span>}
                            {log.photoKey && (
                              <PhotoThumb photoKey={log.photoKey} onOpen={() => setLightboxKey(log.photoKey!)} />
                            )}
                          </span>
                          {canDeleteLog(log, me?.userId) && (
                            <button
                              type="button"
                              onClick={() => setPendingDelete(log)}
                              className="shrink-0 cursor-pointer rounded p-1 text-gray-400 opacity-0 transition-opacity hover:bg-red-50 hover:text-red-600 focus:opacity-100 group-hover:opacity-100 dark:hover:bg-red-950"
                              title="Delete"
                              aria-label="Delete entry"
                            >
                              <Trash2 size={15} />
                            </button>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>

      {backfillOpen && me && (
        <BackfillModal
          types={types}
          members={members}
          currentUserId={me.userId}
          defaultPerformedBySelf={settings.defaultPerformedBySelf}
          onClose={() => setBackfillOpen(false)}
          onSaved={() => {
            setBackfillOpen(false);
            void load(true);
          }}
        />
      )}

      {reportType && (
        <ItemReportModal
          key={reportType.activityTypeId}
          type={reportType}
          timezone={household.timezone}
          members={members}
          onClose={() => setReportType(null)}
        />
      )}

      {lightboxKey && <PhotoLightbox photoKey={lightboxKey} onClose={() => setLightboxKey(null)} />}

      {pendingDelete && (
        <ConfirmDialog
          title="Delete this entry?"
          message="It will be removed from your log."
          confirmLabel="Delete"
          destructive
          busy={deleting}
          onConfirm={() => void confirmDelete()}
          onCancel={() => setPendingDelete(null)}
        />
      )}
    </div>
  );
}
